/**
 * BoltFab — Global floating action button that opens a BOLT AI chat panel.
 *
 * Replaces the old AIAssistant FAB and per-page ai-corner buttons.
 * Renders on all dashboard routes. Opens a fixed chat panel using BoltChat.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { X, Clock } from 'lucide-react';
import BoltChat from './command-center/Bolt/BoltChat';
import BoltConversationSidebar from './command-center/Bolt/BoltConversationSidebar';
import JobsiteSelectorDropdown from './command-center/Bolt/JobsiteSelectorDropdown';
import { useJobsites } from '../hooks/useJobsites';
import './BoltFab.css';

// User-resizable panel: persisted size + clamps.
const PANEL_SIZE_KEY = 'vh_bolt_panel_size';
const PANEL_MIN_W = 360;
const PANEL_MIN_H = 420;
// The previous fixed default — treated as "never intentionally set" so existing
// users migrate to the new ~half-screen default rather than being stuck at it.
const LEGACY_DEFAULT = { width: 420, height: 600 };

function clampPanelSize(w, h) {
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1280;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  const maxW = Math.max(PANEL_MIN_W, vw - 32); // leaves ~1rem margins
  const maxH = Math.max(PANEL_MIN_H, vh - 128); // matches CSS max-height guard
  return {
    width: Math.round(Math.min(Math.max(w, PANEL_MIN_W), maxW)),
    height: Math.round(Math.min(Math.max(h, PANEL_MIN_H), maxH)),
  };
}

// Default ≈ half the screen: ~50% of viewport width, most of its height.
function defaultPanelSize() {
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1280;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  return clampPanelSize(Math.round(vw * 0.5), Math.round(vh * 0.8));
}

function readPanelSize() {
  try {
    const raw = localStorage.getItem(PANEL_SIZE_KEY);
    const s = raw ? JSON.parse(raw) : null;
    if (s && Number.isFinite(Number(s.width)) && Number.isFinite(Number(s.height))) {
      // Migrate the old fixed default to the new ~half-screen default.
      if (Number(s.width) === LEGACY_DEFAULT.width && Number(s.height) === LEGACY_DEFAULT.height) {
        return defaultPanelSize();
      }
      return clampPanelSize(Number(s.width), Number(s.height));
    }
  } catch {
    /* ignore malformed storage */
  }
  return defaultPanelSize();
}

export default function BoltFab() {
  const [isOpen, setIsOpen] = useState(false);
  const [pressKey, setPressKey] = useState(0); // bumps each click to replay the press-ring animation
  const [panelSize, setPanelSize] = useState(readPanelSize);

  // Drag the top-left corner to resize (panel is anchored bottom-right, so
  // dragging up/left grows it). Persist ONLY on an actual user resize — so the
  // default isn't "locked in" just by opening, and future default changes apply.
  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startY = e.clientY;
    const startW = panelSize.width;
    const startH = panelSize.height;
    let last = { width: startW, height: startH };
    document.body.style.userSelect = 'none';
    const onMove = (ev) => {
      last = clampPanelSize(startW + (startX - ev.clientX), startH + (startY - ev.clientY));
      setPanelSize(last);
    };
    const onUp = () => {
      document.body.style.userSelect = '';
      try {
        localStorage.setItem(PANEL_SIZE_KEY, JSON.stringify(last));
      } catch {
        /* ignore */
      }
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }, [panelSize]);
  const location = useLocation();
  const params = useParams();

  // Extract jobsiteId from URL params or route path
  const urlJobsiteId = useMemo(() => {
    if (params.siteId) return params.siteId;
    if (params.jobsiteId) return params.jobsiteId;
    const match = location.pathname.match(/\/sites\/([a-f0-9]{24})/);
    return match ? match[1] : null;
  }, [params, location.pathname]);

  const [selectedJobsiteId, setSelectedJobsiteId] = useState(urlJobsiteId);
  const [showHistory, setShowHistory] = useState(false);
  const [activeConversationId, setActiveConversationId] = useState(null);

  // Sync with URL when navigating between jobsites
  useEffect(() => {
    if (urlJobsiteId) setSelectedJobsiteId(urlJobsiteId);
  }, [urlJobsiteId]);

  useEffect(() => {
    setActiveConversationId(null);
  }, [selectedJobsiteId]);

  // Fetch jobsites for the selector
  const { data: jobsitesData } = useJobsites();
  const jobsites = useMemo(() => {
    const raw =
      jobsitesData?.data?.jobsites ||
      jobsitesData?.jobsites ||
      (Array.isArray(jobsitesData?.data) ? jobsitesData.data : null) ||
      (Array.isArray(jobsitesData) ? jobsitesData : null) ||
      [];
    return Array.isArray(raw) ? raw : [];
  }, [jobsitesData]);

  const isDashboardRoute = useCallback((path) => {
    const dashboardPaths = ['/command-center', '/foreman'];
    return (
      dashboardPaths.some((p) => path.startsWith(p)) || path.includes('/dashboard')
    );
  }, []);

  // Only render on dashboard routes
  if (!isDashboardRoute(location.pathname)) {
    return null;
  }

  return (
    <>
      {/* Overlay when open */}
      {isOpen && (
        <div
          className="bolt-fab-overlay"
          onClick={() => {
            setShowHistory(false);
            setIsOpen(false);
          }}
        />
      )}

      {/* Chat Panel */}
      <div
        className={`bolt-fab-panel ${isOpen ? 'bolt-fab-panel--open' : ''}`}
        style={{ width: panelSize.width, height: panelSize.height }}
      >
        <div
          className="bolt-fab-panel__resize"
          onPointerDown={handleResizeStart}
          role="separator"
          aria-label="Drag to resize chat panel"
          title="Drag to resize"
        />
        <div className="bolt-fab-panel__header">
          <div className="bolt-fab-panel__brand">
            <span className="bolt-fab-panel__mark">
              <span className="bolt-mark" role="img" aria-label="Bolt" />
            </span>
            <span className="bolt-fab-panel__titles">
              <span className="bolt-fab-panel__title">Bolt</span>
              <span className="bolt-fab-panel__subtitle">AI jobsite copilot</span>
            </span>
          </div>
          <JobsiteSelectorDropdown
            jobsites={jobsites}
            selectedId={selectedJobsiteId}
            onChange={setSelectedJobsiteId}
            className="bolt-fab-panel__selector"
          />
          <button
            type="button"
            className="bolt-fab-panel__history"
            onClick={() => setShowHistory((h) => !h)}
            aria-label="Conversation history"
          >
            <Clock size={16} />
          </button>
          <button
            className="bolt-fab-panel__close"
            onClick={() => {
              setShowHistory(false);
              setIsOpen(false);
            }}
            aria-label="Close BOLT"
          >
            <X size={18} />
          </button>
        </div>
        <div className="bolt-fab-panel__body">
          {isOpen && (
            <>
              {showHistory && (
                <div className="bolt-fab-history-drawer">
                  <BoltConversationSidebar
                    jobsiteId={selectedJobsiteId}
                    activeConversationId={activeConversationId}
                    onSelect={(convId) => {
                      setActiveConversationId(convId);
                      setShowHistory(false);
                    }}
                    onNew={() => {
                      setActiveConversationId(null);
                      setShowHistory(false);
                    }}
                    isOpen
                  />
                </div>
              )}
              <BoltChat
                jobsiteId={selectedJobsiteId}
                onConversationChange={setActiveConversationId}
                initialConversationId={activeConversationId}
              />
            </>
          )}
        </div>
      </div>

      {/* FAB Button */}
      <button
        className={`bolt-fab ${isOpen ? 'bolt-fab--active' : ''}`}
        onClick={() => {
          setPressKey((k) => k + 1);
          setIsOpen(!isOpen);
        }}
        aria-label={isOpen ? 'Close BOLT AI' : 'Open BOLT AI'}
      >
        {pressKey > 0 && <span key={pressKey} className="bolt-fab__ring" aria-hidden="true" />}
        {isOpen ? (
          <X size={24} />
        ) : (
          <span className="bolt-mark bolt-fab__mark" role="img" aria-hidden="true" />
        )}
      </button>
    </>
  );
}
